import {useEffect, useState} from 'react'
import type {StackData} from '../page-data/payloads/header'
import {copyRichText} from '../helpers/copy-rich-text'

const copyStatusMsDelay = 2000

export type CopyStackLinksStatus = 'success' | 'error'

type StackPullLink = Pick<StackData['pulls'][number], 'number' | 'title' | 'url'>

function buildClipboardContent(pulls: StackPullLink[]): {html: string; plainText: string} {
  const htmlContainer = document.createElement('div')
  const urls = pulls.map(pull => {
    const url = new URL(pull.url, window.location.origin)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      throw new Error(`Unsupported pull request URL protocol: ${url.protocol}`)
    }

    const href = url.toString()
    const row = document.createElement('div')
    const anchor = document.createElement('a')
    anchor.href = href
    anchor.textContent = `#${pull.number}`
    row.append(anchor, `: ${pull.title}`)
    htmlContainer.append(row)

    return href
  })

  return {
    html: htmlContainer.innerHTML,
    plainText: urls.join('\n'),
  }
}

export function useCopyStackLinks({pulls}: {pulls?: StackPullLink[]}) {
  const [copyStatus, setCopyStatus] = useState<CopyStackLinksStatus>()

  useEffect(() => {
    if (!copyStatus) return

    const timeoutId = setTimeout(() => setCopyStatus(undefined), copyStatusMsDelay)
    return () => clearTimeout(timeoutId)
  }, [copyStatus])

  const copyStackLinks = async () => {
    setCopyStatus(undefined)

    try {
      const content = buildClipboardContent(pulls ?? [])
      await copyRichText(content)
      setCopyStatus('success')
    } catch {
      setCopyStatus('error')
    }
  }

  return {
    copyStatus,
    copyStackLinks,
    resetCopyStatus: () => setCopyStatus(undefined),
  }
}
